import { FORM_SORT_ANSWERS_COOKIE_KEY } from '@/constants';
import {
  createCookieExpirationDate,
  setLeadIdToCookies,
} from '@/hooks/useLeadIdWithCookies';
import { devLog } from '@/lib/logging';
import type { FormSortAnswers, FormSortEvent } from '@/types/forms';
import { pushFormEventToDataLayer } from '@/utils/dataLayer';
import { TrackingHelper } from '@/utils/tracking';
import { useRef } from 'react';
import { FormSortAnswersService } from '../services/FormSortAnswersService';
import { LeadCreationService } from '../services/LeadCreationService';
import { RedirectService } from '../services/RedirectService';
import { sessionStorageService } from '../services/SessionStorageService';
import { UpdateLeadService } from '../services/UpdateLeadService';

const COOKIE_EXPIRATION_MINS = 20;

const QUOTE_PAGE_PATH = '/sign-up/get-an-estimate/quote';

type FlowFinalizedDataLayerEvent = {
  event: 'FlowFinalized';
  form_type: 'sign_up';
  intakeFormAnswers: Pick<
    FormSortAnswers,
    | 'contact_details'
    | 'date_of_birth'
    | 'members'
    | 'visited_locations'
    | 'concierge_healthcare_familiarity'
    | 'healthcare_priorities'
  >;
};

type StepCompletedDataLayerEvent = {
  event: 'StepCompleted';
  form_type: 'sign_up';
  intakeFormAnswers: Pick<
    FormSortAnswers,
    | 'contact_details'
    | 'date_of_birth'
    | 'members'
    | 'visited_locations'
    | 'concierge_healthcare_familiarity'
    | 'healthcare_priorities'
  >;
};

export const useIntakeFormHooks = () => {
  // Use refs to avoid rerenders for the operation queue and leadCreated flag
  const leadCreated = useRef(false);

  const handleStepCompleted = async (data: FormSortEvent) => {
    devLog('Step completed with data:', data);
    sessionStorageService.setFormSortAnswers(data?.answers || {});

    const answers = data.answers;

    try {
      pushFormEventToDataLayer({
        eventName: 'StepCompleted' as StepCompletedDataLayerEvent['event'],
        form_type: 'sign_up' as StepCompletedDataLayerEvent['form_type'],
        data: {
          contact_details: answers?.contact_details,
          date_of_birth: answers?.date_of_birth,
          members: answers?.members || [],
          visited_locations: answers?.visited_locations || [],
          concierge_healthcare_familiarity:
            answers?.concierge_healthcare_familiarity,
          healthcare_priorities: answers?.healthcare_priorities || [],
          stepIndex: data.stepIndex,
        } as StepCompletedDataLayerEvent['intakeFormAnswers'],
      });
    } catch (error) {
      console.error('Error sending data to dataLayer:', error);
    }

    // Create lead if contact details are present
    if (answers?.contact_details) {
      const contactDetails = answers.contact_details;
      // Prevent duplicate lead creation attempts
      if (!leadCreated.current) {
        devLog('Contact Details:', contactDetails);
        leadCreated.current = true;

        const trackingData = TrackingHelper.getTrackingData();
        const leadCreationService = new LeadCreationService(
          contactDetails,
          trackingData
        );

        const response = await leadCreationService
          .createLead()
          .then(result => {
            devLog('Lead created:', result);
            if (result.leadId) {
              sessionStorageService.setLeadId(result.leadId);

              setLeadIdToCookies(result.leadId);
            } else {
              throw new Error('Lead creation failed');
            }
          })
          .catch(error => {
            devLog('Error creating lead:', error);
            leadCreated.current = false; // Reset flag on error so it can be retried
          });

        return response;
      }
    }
  };

  async function handleFlowFinalized(data: FormSortEvent) {
    devLog('Flow finalized with data:', data);

    sessionStorageService.setFormSortAnswers(data?.answers || {});

    cookieStore.set({
      name: FORM_SORT_ANSWERS_COOKIE_KEY,
      value: JSON.stringify(data.answers),
      expires: createCookieExpirationDate(COOKIE_EXPIRATION_MINS),
    });

    // Update the lead now that we have all the information
    const answers = data.answers;

    try {
      pushFormEventToDataLayer({
        eventName: 'FlowFinalized' as FlowFinalizedDataLayerEvent['event'],
        form_type: 'sign_up' as FlowFinalizedDataLayerEvent['form_type'],
        data: {
          contact_details: answers?.contact_details,
          date_of_birth: answers?.date_of_birth,
          members: answers?.members || [],
          visited_locations: answers?.visited_locations || [],
          concierge_healthcare_familiarity:
            answers?.concierge_healthcare_familiarity,
          healthcare_priorities: answers?.healthcare_priorities || [],
        } as FlowFinalizedDataLayerEvent['intakeFormAnswers'],
      });
    } catch (error) {
      console.error('Error sending data to dataLayer:', error);
    }

    try {
      const leadId = sessionStorageService.getLeadId();
      if (!leadId) {
        throw new Error('No lead ID found in session storage.');
      }

      const answersService = new FormSortAnswersService(answers);

      devLog('Updating lead...');
      await new UpdateLeadService({
        answersService,
        leadId,
        promotion: '',
      }).updateLead();

      devLog('Lead updated successfully');
    } catch (error) {
      devLog('Error updating lead:', error);
      // Continue to redirect even if lead update fails
      // The user can still see the quote and try checkout later
    }

    // Redirect to quote page
    RedirectService.redirectTo(QUOTE_PAGE_PATH);
  }

  return {
    handleStepCompleted,
    handleFlowFinalized,
  };
};
